import React, { useCallback, useEffect, useState } from "react";
import * as R from "ramda";
import { Box, Button } from "@chakra-ui/react";
import { noop, isNilOrEmpty, useSessionCtx, useSessionJWT } from "utils";
import { PM_STATE, getPaymentMethodAnyState } from "api/payments";
import { logInfo, logError } from "api/accounts-customer";
import { useModal } from "components/Modal";
import { usePolling } from "utils/usePolling";
import { TiliaLogo } from "components/Logos";

const featureFlag3ds = (settings) =>
  R.equals(true, R.defaultTo(false, R.path(["feature_flags", "3DS"], settings)));

const pollingTimeoutSeconds = (settings) =>
  R.defaultTo(0, R.path(["3ds", "3ds_polling_timeout_seconds"], settings));
const pmUnverified = (payload) => R.propEq("pm_state", PM_STATE.UNVERIFIED, payload);

const finalStatus = [PM_STATE.ACTIVE, PM_STATE.INACTIVE];
const isFinalStatus = (payload) => {
  const state = R.path(["payload", "pm_state"], payload);
  // if there is no state (i.e. no payload), then assume the pm was set to INACTIVE and is final
  return isNilOrEmpty(state) || R.any(R.equals(state))(finalStatus);
};

export const requires3DS = (settings, paymentMethodPayload) => {
  if (!featureFlag3ds(settings)) {
    return false;
  }
  if (!pmUnverified(paymentMethodPayload)) {
    return false;
  }
  if (isNilOrEmpty(R.prop("approval_url", paymentMethodPayload))) {
    throw new Error("missing approval url for unverified payment method");
  }
  return true;
};

const ErrorFrame = ({ onClick }) => {
  return (
    <Box
      sx={{
        height: "calc(100% - 4px)",
        width: "calc(100% - 4px)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        "& > *": {
          margin: "8px",
        },
      }}
    >
      <TiliaLogo />
      <div>Apologies, there has been an error.</div>
      <div>Please click below and try again.</div>
      <Button
        variant="outline"
        colorScheme="gray"
        onClick={onClick}
      >
        OK
      </Button>
    </Box>
  );
};

export const ThreeDS2 = ({ settings, data = {}, onFinalStatus, onError }) => {
  const { sessionJWT } = useSessionJWT();
  const { debug } = useSessionCtx();
  const [Modal, openModal, closeModal] = useModal();

  debug("ThreeDS2__settings: ", settings, data);
  const { approvalUrl, paymentMethodId } = data;
  // ---------

  const pollingFn = useCallback(
    async (pollingInfo) => {
      debug("3DS__pollingFn__pollingInfo", pollingInfo);
      const pmResponse = await getPaymentMethodAnyState(sessionJWT, paymentMethodId);
      if (isFinalStatus(pmResponse)) {
        pollingInfo.stopPolling();
        onFinalStatus(pmResponse);
      }
    },
    [debug, paymentMethodId, onFinalStatus, sessionJWT]
  );

  const [showError, setShowError] = useState(false);
  const dismissAfterError = () => {
    debug("ThreeDS2__dismissAfterError");
    onError(false);
    closeModal();
    setShowError(false);
    resetPolling();
  };

  const handlePollingTimeout = () => {
    debug("3DS_POLLING_TIMEOUT", paymentMethodId);
    logError(sessionJWT, { flowLoc: "3DS_POLLING_TIMEOUT", paymentMethodId });
    setShowError(true);
  };

  const [startPolling, resetPolling] = usePolling(pollingFn, {
    pollingTimeoutMS: 1000 * pollingTimeoutSeconds(settings),
    onTimeout: handlePollingTimeout,
  });

  // ---------

  const init = useCallback(() => {
    openModal();
    startPolling();
  }, [openModal, startPolling]);

  useEffect(() => {
    if (!isNilOrEmpty(approvalUrl)) {
      logInfo(sessionJWT, { flowLoc: "3DS_INIT", approvalUrl, paymentMethodId });
      init();
    }
  }, [init, approvalUrl, paymentMethodId, sessionJWT]);

  return (
    <>
      <Modal onRequestClose={noop}>
        <div
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "stretch",
          }}
        >
          {showError ? (
            <ErrorFrame onClick={dismissAfterError} />
          ) : (
            <iframe
              data-testid="3ds-iframe"
              style={{
                border: "0",
                height: "calc(100% - 4px)",
                width: "calc(100% - 4px)",
              }}
              title="3DS-iframe"
              src={approvalUrl}
            />
          )}
        </div>
      </Modal>
    </>
  );
};
